import { z } from 'zod/v4';

export enum MatchType {
  LEAGUE = 'LEAGUE',
  TOURNAMENT = 'TOURNAMENT',
}

export enum LeagueDivision {
  MULTIPLEX = 'MULTIPLEX',
  ADVANCED = 'ADVANCED',
  MAIN = 'MAIN',
  INTERMEDIATE = 'INTERMEDIATE',
  ENTRY = 'ENTRY',
  OPEN10 = 'OPEN10',
  OPEN9 = 'OPEN9',
  OPEN1_8 = 'OPEN1_8',
}

export const MatchDTO = z.object({
  id: z.string(),
  date: z.string(),
  type: z.enum(MatchType),
  division: z.enum(LeagueDivision).optional(),
  eventName: z.string().optional(),
  description: z.string(),
  link: z.string().nullable().optional(),
  matchStreamers: z.array(z.string()).nullable().optional(),
  user: z
    .object({
      id: z.string(),
      discordId: z.string(),
      username: z.string(),
      avatar: z.string(),
      accessToken: z.string(),
    })
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const MatchModel = z.object({
  id: z.string(),
  date: z.date(),
  type: z.enum(MatchType),
  division: z.enum(LeagueDivision).optional(),
  eventName: z.string().optional(),
  description: z.string(),
  link: z.string().nullable().optional(),
  matchStreamers: z.array(z.string()).nullable().optional(),
  user: z
    .object({
      id: z.string(),
      discordId: z.string(),
      username: z.string(),
      avatar: z.string(),
      accessToken: z.string(),
    })
    .optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export const MatchCreateInputSchema = z.object({
  date: z.date(),
  type: z.enum(MatchType),
  division: z.enum(LeagueDivision).optional(),
  eventName: z.string().optional(),
  description: z.string(),
  link: z.string().nullable().optional(),
});

export const MatchUpdateInputSchema = z.object({
  id: z.string(),
  date: z.date().optional(),
  type: z.enum(MatchType).optional(),
  division: z.enum(LeagueDivision).optional(),
  eventName: z.string().optional(),
  description: z.string().optional(),
  link: z.string().nullable().optional(),
});

export type MatchDTO = z.infer<typeof MatchDTO>;
export type MatchModel = z.infer<typeof MatchModel>;
export type MatchCreateInput = z.infer<typeof MatchCreateInputSchema>;
export type MatchUpdateInput = z.infer<typeof MatchUpdateInputSchema>;

export class Match implements MatchModel {
  public id: string;
  public date: Date;
  public type: MatchType;
  public division?: LeagueDivision;
  public eventName?: string;
  public description: string;
  public link?: string | null;
  public matchStreamers?: string[] | null;
  public createdAt: Date;
  public updatedAt: Date;
  public user?: {
    id: string;
    discordId: string;
    username: string;
    avatar: string;
    accessToken: string;
  };

  constructor(
    match: MatchDTO & {
      user?: {
        id: string;
        discordId: string;
        username: string;
        avatar: string;
        accessToken: string;
      };
    }
  ) {
    this.id = match.id;
    this.date = new Date(match.date);
    this.type = match.type as MatchType;
    this.division = (match.division as LeagueDivision) ?? undefined;
    this.eventName = match.eventName ?? undefined;
    this.description = match.description;
    this.link = match.link ?? undefined;
    this.matchStreamers = Array.isArray(match.matchStreamers) ? match.matchStreamers : undefined;
    this.createdAt = new Date(match.createdAt);
    this.updatedAt = new Date(match.updatedAt);
    this.user = match.user;
  }

  public static validate(matchData: Match): Match {
    const match = MatchModel.safeParse(matchData);
    if (!match.success) {
      throw new Error(`Invalid match data: ${match.error.message}`);
    }
    return matchData;
  }
}
